import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Headset } from 'lucide-react';
import { supportAPI } from '@/lib/api';
import { SUPPORT_READ_EVENT } from '@/hooks/useSupportUnread';
import { Button } from '@/components/ui/button';
import SupportThread from '@/components/support/SupportThread';

/**
 * Chat with the African Farm support team (the header's Support button).
 * "Ask about this product" in the marketplace opens this screen with the
 * product attached to the next message; "Become a cluster manager" (Profile)
 * opens it with the request already typed.
 */
export default function SupportChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [attachment, setAttachment] = useState(location.state?.product || null);

  const load = useCallback(async (after) => (await supportAPI.thread(after)).data, []);
  const send = useCallback(async (draft) => (await supportAPI.send(draft)).data, []);
  const markRead = useCallback(async () => {
    try {
      await supportAPI.read();
      window.dispatchEvent(new Event(SUPPORT_READ_EVENT));
    } catch {
      // The badge corrects itself on the next poll.
    }
  }, []);

  return (
    <div className="flex flex-col pb-28" data-testid="support-chat-page">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-3 py-2 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label={t('chat.back')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="w-9 h-9 rounded-full bg-farm-primary/10 grid place-items-center">
          <Headset className="w-5 h-5 text-farm-primary" />
        </div>
        <div className="min-w-0">
          <p className="font-semibold truncate">{t('supportChat.title')}</p>
          <p className="text-xs text-muted-foreground">{t('supportChat.subtitle')}</p>
        </div>
      </div>
      <SupportThread
        threadKey="me"
        load={load}
        send={send}
        markRead={markRead}
        attachment={attachment}
        onClearAttachment={() => setAttachment(null)}
        senderLabel={(m) => (m.mine ? null : t('supportChat.teamName'))}
        emptyText={t('supportChat.empty')}
        onProductOpen={(p) => navigate(`/marketplace?product=${encodeURIComponent(p.id)}`)}
        initialDraft={location.state?.draft || ''}
      />
    </div>
  );
}
